import React, { type FC, useState, useEffect, useContext } from 'react';

import { useMotionValue, motion, MotionConfig } from 'motion/react';
import { GatsbyImage, type IGatsbyImageData } from 'gatsby-plugin-image';
import kebabCase from 'lodash/kebabCase';
import { useSmartlingStrings } from '@lucid/hooks';

import Box from '../../box/box';
import Text from '../../text/text';
import LayoutContext from '../../context/layout-context';

export type IntegrationItem = {
  /** The packageId, linking the item to its respective Integration page */
  id: string;
  /** The Integration title */
  title: string;
  /** A small image (logo) provided for the Integration Item */
  image: IGatsbyImageData;
};

export type IntegrationsBannerRowProps = {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The total duration (seconds) of the animation */
  duration: number;
  /** The row of integration items */
  row: IntegrationItem[];
  /** The direction of the animation */
  direction: 'RTL' | 'LTR';
  /** External override for the row animation play state */
  willAllowPause: boolean;
};

const IntegrationsBannerRow: FC<IntegrationsBannerRowProps> = ({
  className,
  duration,
  row,
  direction,
  willAllowPause,
}) => {
  const currentRow = useMotionValue(0);
  const isRightToLeft = direction === 'RTL';

  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_ct_add_on_pause_animation_label',
      'mktg_ct_add_on_play_animation_label',
      'mktg_integration_link_aria_label',
    ],
    shortCode,
  });

  const [animation, setAnimation] = useState('activeLTR');
  const [hasHover, setHasHover] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [resumePlayPosition, setResumePlayPosition] = useState<string | number>(0);
  const [shouldPause, setShouldPause] = useState(false);

  let pauseTimeout: NodeJS.Timeout | undefined = undefined;

  const handleMouseEnter = () => {
    setHasHover(true);

    pauseTimeout = setTimeout(() => {
      setShouldPause(true);
    }, 800);

    return () => clearTimeout(pauseTimeout);
  };

  const handleMouseLeave = () => {
    clearTimeout(pauseTimeout);
    setHasHover(false);
    setShouldPause(false);
  };

  useEffect(() => {
    setResumePlayPosition(currentRow.get());

    if (willAllowPause) setAnimation('inactive');
    else {
      if (!hasHover) {
        clearTimeout(pauseTimeout);
        setShouldPause(false);
      }

      if (shouldPause && hasHover) {
        setResumePlayPosition(currentRow.get());
        setAnimation('inactive');
      } else {
        setAnimation(`active${direction}`);
      }
    }

    return () => {
      window.clearTimeout(pauseTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHover, shouldPause, willAllowPause]);

  return (
    <li
      css={{
        display: 'flex',
        position: 'relative',
        minWidth: 'max-content',
        listStyleType: 'none',
        margin: 0,
      }}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <MotionConfig
        transition={{
          duration,
          ease: 'linear',
          type: 'keyframes',
          repeat: Infinity,
          repeatType: 'loop',
        }}
      >
        <motion.div
          animate={animation}
          css={{ display: 'flex', position: 'relative' }}
          drag="x"
          onDragEnd={() => setIsDragging(false)}
          onDragStart={() => setIsDragging(true)}
          onMouseEnter={() => handleMouseEnter()}
          onMouseLeave={() => handleMouseLeave()}
          onViewportLeave={() => setResumePlayPosition('')}
          style={{ x: currentRow }}
          variants={{
            activeLTR: { x: [resumePlayPosition || '0%', '100%'] },
            activeRTL: { x: [resumePlayPosition || '0%', '-100%'] },
            inactive: { x: resumePlayPosition },
          }}
        >
          {Array.from({ length: 2 }, (_, i) => (
            <Box
              key={`integration-row-segment-${kebabCase(row[0].title)}-${i}`}
              as="ul"
              css={theme => [
                {
                  display: 'flex',
                  gap: theme.space.s24,
                  position: i === 1 ? 'absolute' : 'relative',
                  scale: 1,
                  marginLeft: i === 1 ? theme.space.s24 : 0,
                  transform: i === 0 ? 'translateX(-100%)' : '',
                },
                isRightToLeft && {
                  marginLeft: i === 0 ? theme.space.s24 : 0,
                  transform: i === 0 ? 'translateX(100%)' : '',
                },
              ]}
            >
              {row.map(integration => (
                <motion.li
                  key={`integration-${integration.id}-${i}`}
                  css={{
                    listStyleType: 'none',
                    visibility: 'hidden',
                    flex: '0 0 auto',
                  }}
                  draggable={false}
                  onMouseDown={e => {
                    e.preventDefault();
                  }}
                >
                  <motion.a
                    aria-label={withSmartling('mktg_integration_link_aria_label', { title: integration.title })}
                    css={theme => ({
                      display: 'flex',
                      textDecoration: 'none',
                      alignItems: 'center',
                      gap: theme.space.s12,
                      background: theme.colors.coolGrey[5],
                      padding: `${theme.space.s16}px ${theme.space.s24}px`,
                      transition: 'background 150ms ease-out',
                      '&:hover': {
                        background: theme.colors.coolGrey[10],
                      },
                      '&:focus': {
                        transition: 'background 0s',
                        background: theme.colors.coolGrey[20],
                      },
                    })}
                    href={`https://lucid.co/marketplace/${integration.id?.split('-')[0]}/${kebabCase(integration.title)}`}
                    onClick={e => {
                      if (isDragging) e.preventDefault();
                    }}
                    onViewportEnter={e => e?.target.setAttribute('style', 'visibility: visible;')}
                    onViewportLeave={e => e?.target.setAttribute('style', 'visibility: hidden;')}
                  >
                    <GatsbyImage
                      alt={integration.title}
                      css={theme => ({
                        width: '32px',
                        height: '32px',
                        borderRadius: theme.radii.r8,
                      })}
                      image={integration.image}
                    />
                    <Text css={{ width: 'fit-content', margin: 0 }} variant="interactiveM500">
                      {integration.title}
                    </Text>
                  </motion.a>
                </motion.li>
              ))}
            </Box>
          ))}
        </motion.div>
      </MotionConfig>
    </li>
  );
};

export { IntegrationsBannerRow };
export default IntegrationsBannerRow;
